import assertNever from "assert-never";
import { ChronoUnit, Duration, LocalDate } from "@js-joda/core";
import * as messages from "../../messages";
import { AgencyId, TrainingCenterBundleItemId } from "../../messages";
import {
  TrainingCenterDisplayLanguage,
  TrainingCenterTranslated,
} from "../../messages/TrainingCenter";
import { STRINGS } from "../../strings";
import { Agency } from "../../system/AppState/AppAgenciesState";
import {
  TrainingCenterBundleCourse,
  TrainingCenterBundleItem,
  TrainingCenterCaregiverBundle,
} from "../../system/AppState/AppStateTrainingCenter";
import { translate } from "../../translate";
import { assertDefined } from "../../utils/assertUndefinedable";
import { HashMap } from "../../utils/HashMap";
import { KeyedList, List } from "../../utils/List";
import { memoize1 } from "../../utils/memoize";
import {
  FALLBACK_LANGUAGE,
  lookupTranslationOptional,
  lookupTranslationRequired,
  nullToUndefined,
  TrainingCenterLanguagePreference,
} from "./TrainingCenterLanguageLookup";

export interface PartitionedBundles {
  required: List<TrainingCenterCaregiverBundle>;
  completed: List<TrainingCenterCaregiverBundle>;
}

export function partitionBundles(bundles: List<TrainingCenterCaregiverBundle>): PartitionedBundles {
  let required = List.empty<TrainingCenterCaregiverBundle>();
  let completed = List.empty<TrainingCenterCaregiverBundle>();
  for (const bundle of bundles) {
    if (bundle.completionCertificate !== null) {
      completed = completed.append(bundle);
    } else {
      required = required.append(bundle);
    }
  }
  return {
    required: required,
    completed: completed,
  };
}

/**
 * Convert bundle items into separated courses.
 * a course is considered as chunk of bundle items that the first item has section header.
 *
 * For example:
 *      Course 1:
 *          Bundle item with section header
 *          Bundle item without section header
 *          Bundle item without section header
 *      Course 2:
 *          Bundle item with section header
 *          Bundle item without section header
 *      etc...
 *
 *  NOTE - If there are bundle items before a section header, they'll be moved to "orphans"
 */
export function convertBundleItemsToCourses(
  bundleItems: KeyedList<TrainingCenterBundleItem, "bundleItemId">
): {
  courses: List<TrainingCenterBundleCourse>;
  orphans: List<TrainingCenterBundleItem>;
} {
  let courses: List<TrainingCenterBundleCourse> = List.empty();
  let orphans: List<TrainingCenterBundleItem> = List.empty();

  for (const bundleItem of bundleItems) {
    const englishDisplayData = bundleItem.displayData.find(
      (x) => x.language === TrainingCenterDisplayLanguage.wrap("English")
    );

    if (englishDisplayData !== undefined && englishDisplayData.data.sectionHeader !== null) {
      courses = courses.append({
        displayData: bundleItem.displayData.mapKeyedList("language", (displayData) => ({
          language: displayData.language,
          data: {
            sectionHeader: displayData.data.sectionHeader,
            sectionDescription: displayData.data.sectionDescription,
          },
        })),
        bundleItems: [bundleItem],
      });
      continue;
    }

    const lastCourse = courses.last();

    if (lastCourse !== undefined) {
      lastCourse.bundleItems.push(bundleItem);
    } else {
      orphans = orphans.append(bundleItem);
    }
  }

  return {
    courses: courses,
    orphans: orphans,
  };
}

export function isTrainingCenterCourseCompleted(course: TrainingCenterBundleCourse) {
  const hasNonCompletedItems = course.bundleItems.some((x) => !x.itemCompleted);
  return !hasNonCompletedItems;
}

export function getNonNullableEnglishCourseName(course: TrainingCenterBundleCourse) {
  const translation = lookupTranslationRequired(
    { preferredLanguage: TrainingCenterDisplayLanguage.wrap("English") },
    course.displayData,
    (x) => x.sectionHeader
  );

  if (translation === null) {
    throw new Error("Course English name should not be null");
  }

  return translation;
}

export function getCourseName(
  course: TrainingCenterBundleCourse,
  languagePreference: TrainingCenterLanguagePreference
) {
  return lookupTranslationRequired(languagePreference, course.displayData, (x) => x.sectionHeader);
}

export function getCourseDescription(
  course: TrainingCenterBundleCourse,
  languagePreference: TrainingCenterLanguagePreference
) {
  return lookupTranslationRequired(
    languagePreference,
    course.displayData,
    (x) => x.sectionDescription
  );
}

export function getTotalCompleteLessonsOfCourse(course: TrainingCenterBundleCourse) {
  return course.bundleItems.filter((bundleItem) => bundleItem.caregiverSignatureDataURL !== null)
    .length;
}

export function isBundleItemUnlocked(
  bundle: TrainingCenterCaregiverBundle,
  bundleItemId: TrainingCenterBundleItemId
): boolean {
  if (bundle.bundleParams.enforceWatchOrder && bundle.completionCertificate === null) {
    for (const item of bundle.bundleItems) {
      if (item.bundleItemId === bundleItemId) {
        return true;
      } else {
        if (!item.itemCompleted && !item.itemExempt) {
          return false;
        }
      }
    }

    // We didn't find the bundleItemId.. This shouldn't happen
    return true;
  } else {
    return true;
  }
}

export function lookupBundleItemThumbnailUrl(
  languagePreference: TrainingCenterLanguagePreference,
  bundleItem: TrainingCenterBundleItem
): string | null {
  const bundleItemThumbnailUrl = lookupTranslationOptional(
    languagePreference,
    bundleItem.displayData,
    (x) => x.thumbnailUrl
  );
  if (bundleItemThumbnailUrl !== null) {
    return bundleItemThumbnailUrl;
  }

  switch (bundleItem.media.type) {
    case "Video":
      return lookupTranslationRequired(languagePreference, bundleItem.media.videoInfo, (x) => x)
        .thumbnailUrl;
    case "PDF":
      return null;
    case "TestOnly":
      return null;
    default:
      return assertNever(bundleItem.media);
  }
}

const DEFAULT_BUNDLE_THUMBNAIL_URL =
  "https://medflyt-assets.s3.amazonaws.com/training-center/medflyt-caregiver-training-center-thumbnail-placeholder.jpg";

export function lookupBundleThumbnailUrl(
  languagePreference: TrainingCenterLanguagePreference,
  bundle: TrainingCenterCaregiverBundle
): string | null {
  const thumbnailUrl = lookupTranslationOptional(
    languagePreference,
    bundle.bundleDisplayData,
    (x) => nullToUndefined(x.thumbnailUrl)
  );

  if (thumbnailUrl !== null) {
    return thumbnailUrl;
  }

  for (const bundleItem of bundle.bundleItems) {
    const bundleItemThumbnailUrl = lookupBundleItemThumbnailUrl(languagePreference, bundleItem);
    if (bundleItemThumbnailUrl !== null) {
      return bundleItemThumbnailUrl;
    }
  }

  return DEFAULT_BUNDLE_THUMBNAIL_URL;
}

function mapHashMap<K, V1, V2>(hashmap: HashMap<K, V1>, fn: (value: V1) => V2): HashMap<K, V2> {
  let result = HashMap.empty<K, V2>();
  for (const [key, value] of hashmap.entries()) {
    result = result.set(key, fn(value));
  }
  return result;
}

export const transformAgencies: (
  agencies: HashMap<AgencyId, Agency>
) => HashMap<AgencyId, messages.Agency> = memoize1((agencies) =>
  mapHashMap(agencies, (a) => a.agency)
);

export function isTrainingCenterBundleCompleted(bundle: TrainingCenterCaregiverBundle) {
  return bundle.completionCertificate !== null;
}

export function getTraningCenterBundleDuration(bundleItems: List<TrainingCenterBundleItem>) {
  let duration = Duration.ofMinutes(0);

  for (const bundleItem of bundleItems) {
    if (bundleItem.timeForCertificateMinutes !== null) {
      duration = duration.plusMinutes(bundleItem.timeForCertificateMinutes);
    }
  }

  return duration;
}

export function getTrainingCenterBundleCompletionRatio(
  bundleItems: TrainingCenterCaregiverBundle["bundleItems"]
): number {
  return bundleItems.filter((x) => x.itemCompleted).length() / bundleItems.length();
}

export function getDisplayData<T>(
  displayData: KeyedList<TrainingCenterTranslated<T>, "language">,
  languagePreference: TrainingCenterLanguagePreference
) {
  const preferredDisplayData = displayData.find(
    (x) => x.language === languagePreference.preferredLanguage
  );

  if (preferredDisplayData !== undefined) {
    return preferredDisplayData;
  }

  const fallbackDisplayData = displayData.find((x) => x.language === FALLBACK_LANGUAGE);

  return assertDefined(fallbackDisplayData);
}

export function formatReadableRelativeDueDate(bundle: TrainingCenterCaregiverBundle) {
  const daysToComplete = LocalDate.now().until(bundle.completionDate, ChronoUnit.DAYS);

  if (bundle.bundleParams.isOrientation) {
    return translate(STRINGS.NO_TIME_LIMIT);
  }

  if (isTrainingCenterBundleCompleted(bundle)) {
    return translate(STRINGS.YOU_HAVE_SUCCESSFULLY_COMPLETED_THIS_TRAINING);
  }

  if (daysToComplete < 0) {
    return translate(STRINGS.YOU_ARE_X_DAYS_LATE).replace(
      "%d",
      Math.abs(daysToComplete).toString()
    );
  }

  if (daysToComplete === 0) {
    return translate(STRINGS.YOU_HAVE_TO_COMPLETE_THIS_TRAINING_TODAY);
  }

  if (daysToComplete === 1) {
    return translate(STRINGS.YOU_HAVE_ONE_DAY_TO_COMPLETE_THIS_TRAINING);
  }

  return translate(STRINGS.YOU_HAVE_X_DAYS_TO_COMPLETE_THIS_TRAINING).replace(
    "%d",
    daysToComplete.toString()
  );
}

export function getTrainingCenterBundleItemStatus(
  bundleItemUnlocked: boolean,
  bundleItem: TrainingCenterBundleItem
): ItemStatus {
  if (bundleItem.itemExempt) {
    return ItemStatus.EXEMPT;
  }

  if (!bundleItemUnlocked) {
    return ItemStatus.LOCKED;
  }

  if (bundleItem.itemCompleted) {
    return ItemStatus.COMPLETE;
  }

  if (!bundleItem.testUnlocked) {
    return ItemStatus.WATCH_REQUIRED;
  }

  if (bundleItem.testNumAttempts > 0) {
    return ItemStatus.TEST_FAILED;
  }

  if (bundleItem.currentTestQuestions.length() === 0) {
    return ItemStatus.SIGNATURE_REQUIRED;
  }

  return ItemStatus.TEST_REQUIRED;
}

export enum ItemStatus {
  LOCKED,
  COMPLETE,
  EXEMPT,
  TEST_FAILED,
  TEST_REQUIRED,
  SIGNATURE_REQUIRED,
  WATCH_REQUIRED,
  SUBMIT_REQUIRED,
}
